// Scoped locale for the PUBLIC respondent surfaces.
//
// Invite links may carry ?lang=<en|es|fr>. Without it, a respondent visitor would
// see every button, prompt and error in whatever locale their own browser store
// happened to hold, whatever language the assessment is in.
//
// Email invites are plain /respond/<token> links with no ?lang at all, so the
// assessment's own language (the voice runner hands it up from the survey) is the
// fallback, and ?lang= is an override on top of it (a reviewer can still force a
// language). Precedence:
//
//   ?lang=  →  the session's language  →  the ambient (browser-store) locale  →  en
//
// The session language arrives from inside the tree, so the provider hands down a
// setter for it.
//
// This wraps ONLY the respondent subtree and deliberately does not touch the global
// locale: an admin opening their own link to check it would otherwise have the whole
// app — dashboard included — silently switched out from under them, and it would
// persist after they navigated away.
import React, { createContext, useCallback, useContext, useState } from "react";
import { IntlContext, IntlProvider } from "react-intl";
import { useLocation } from "react-router-dom";
import English from "../languages/en.json";
import Spanish from "../languages/es.json";
import French from "../languages/fr.json";

const MESSAGES = { en: English, es: Spanish, fr: French };

export const SUPPORTED_LOCALES = Object.keys(MESSAGES);

// A raw language value → a supported locale, or null when it's blank or
// unrecognised. Tolerates regional tags and case — "ES-mx" is Spanish.
export const normalizeLocale = (raw) => {
    if (raw === null || raw === undefined) return null;
    const lc = String(raw).trim().toLowerCase().split(/[-_]/)[0];
    return SUPPORTED_LOCALES.includes(lc) ? lc : null;
};

// ?lang= → a supported locale, or null when it's absent or unrecognised.
export const parseLangParam = (search) => {
    let raw = null;
    try {
        raw = new URLSearchParams(search || "").get("lang");
    } catch (e) {
        return null;
    }
    return raw ? normalizeLocale(raw) : null;
};

// The respondent locale, or null to inherit the ambient one: an explicit ?lang=
// wins over the session's language.
export const resolveRespondentLocale = (search, sessionLanguage) =>
    parseLangParam(search) || normalizeLocale(sessionLanguage);

// Setter for the session's language. A no-op outside the provider, so the
// voice runner can call it unconditionally.
const SessionLanguageContext = createContext(() => {});
export const useSetSessionLanguage = () => useContext(SessionLanguageContext);

export default function RespondentIntlProvider({ children }) {
    const { search } = useLocation();
    const ambient = useContext(IntlContext);
    const [sessionLanguage, setSessionLanguage] = useState(null);
    const setLanguage = useCallback((raw) => setSessionLanguage(normalizeLocale(raw)), []);

    const locale = resolveRespondentLocale(search, sessionLanguage);
    // ALWAYS the same element tree, whichever locale wins: swapping a bare
    // fragment for an IntlProvider when the session language arrives would
    // remount the page, and the voice runner would lose its place.
    const effective = locale || ambient?.locale || "en";
    const messages = locale ? MESSAGES[locale] : ambient?.messages || MESSAGES.en;
    return (
        <SessionLanguageContext.Provider value={setLanguage}>
            <IntlProvider locale={effective} messages={messages} defaultLocale="en">
                {children}
            </IntlProvider>
        </SessionLanguageContext.Provider>
    );
}
